import type React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';

import { errorKindOf, serverMessageOf } from '../../../api/modification';
import {
  MOD_ERROR_AUTH_TEXT,
  MOD_ERROR_CONFLICT_TEXT,
  MOD_ERROR_FORBIDDEN_TEXT,
  MOD_ERROR_INVALID_TEXT,
  MOD_ERROR_NOT_FOUND_TEXT,
  MOD_ERROR_RETRY_TEXT,
  MOD_NOT_AVAILABLE_TEXT,
  MOD_NOT_AVAILABLE_TITLE_TEXT,
  MOD_RETRY_TEXT,
} from '../../../constants/modification.strings';

/**
 * One sentence for any failure a screen catches. A 422 shows the server's own
 * message when it sent one. A 409 does too when asked (the upload link limit is
 * a 409 whose sentence is the gateway's); otherwise a 409 means the record
 * moved on. 503 and a disabled build both read "not available".
 */
export const messageForError = (e: unknown, options: { serverMessageOnConflict?: boolean } = {}): string => {
  switch (errorKindOf(e)) {
    case 'auth':
      return MOD_ERROR_AUTH_TEXT;
    case 'forbidden':
      return MOD_ERROR_FORBIDDEN_TEXT;
    case 'not-found':
      return MOD_ERROR_NOT_FOUND_TEXT;
    case 'conflict':
      return (options.serverMessageOnConflict ? serverMessageOf(e) : null) ?? MOD_ERROR_CONFLICT_TEXT;
    case 'invalid':
      return serverMessageOf(e) ?? MOD_ERROR_INVALID_TEXT;
    case 'unavailable':
    case 'disabled':
      return MOD_NOT_AVAILABLE_TEXT;
    default:
      return MOD_ERROR_RETRY_TEXT;
  }
};

/** The whole-page answer when the feature is off in this build, or the server says it is not configured. */
export const NotAvailable: React.FC = () => (
  <Box sx={{ marginX: { xs: 2, sm: '3.75rem' }, marginY: '2rem' }}>
    <Typography component="h1" variant="h5" sx={{ fontWeight: 700, marginBottom: '0.75rem' }}>
      {MOD_NOT_AVAILABLE_TITLE_TEXT}
    </Typography>
    <Typography>{MOD_NOT_AVAILABLE_TEXT}</Typography>
  </Box>
);

type ErrorNoticeProps = {
  message: string;
  onRetry?: () => void;
};

/** A failure line, with a retry where one makes sense. role="alert" so it is announced. */
export const ErrorNotice: React.FC<ErrorNoticeProps> = ({ message, onRetry }) => (
  <Alert
    severity="error"
    role="alert"
    action={
      onRetry ? (
        <Button color="inherit" size="small" onClick={onRetry} sx={{ textTransform: 'none' }}>
          {MOD_RETRY_TEXT}
        </Button>
      ) : undefined
    }
    sx={{ alignItems: 'center' }}
  >
    {message}
  </Alert>
);
